import { ClaimRow } from 'api/endpoints';
import { SectionType, SubsectionType } from 'pages/ClaimsContractsToolPage/types';
import { Link } from 'react-router-dom';
import { formatDate, ISO_DATE } from 'utils/formatDate';
import { formatMoney } from 'utils/formatNumber';
import { buildSingleClaimPath } from 'utils/hooks/useClaimToolQueryParams';
import { buildSingleContractPath } from 'utils/hooks/useContractToolQueryParams';
import { ContractReviewerData } from '../../reducer/contractReviewerReducer';
import {
  ClaimTallyItem,
  ClaimTallyItemFormatted,
  ContractItemFormatted,
  ContractRowFormatted,
  FiledClaimItemFormatted,
  ScheduledClaimItemFormatted,
} from './Counterparty.types';

export function formatClaimTallyItem(item: ClaimTallyItem): ClaimTallyItemFormatted {
  return {
    ...item,
    admin_formatted: formatMoney(item.admin),
    priority_formatted: formatMoney(item.priority),
    secured_formatted: formatMoney(item.secured),
    count_formatted: formatMoney(item.count),
    total_formatted: formatMoney(item.total),
    unsecured_formatted: formatMoney(item.unsecured),
  };
}

export function formatFiledClaimItem(item: ClaimRow): FiledClaimItemFormatted {
  const singleClaimUrl = buildSingleClaimPath(
    item.id!,
    SectionType.Claims,
    SubsectionType.AllClaims,
  );

  return {
    refLink: <Link to={singleClaimUrl}>{item.referenceNumber}</Link>,
    counterpartyName: item.counterpartyName,
    empty: 'empty',
    currentDebtor: item.currentDebtor,
    subMatchCode: item.subMatchCode,
    majorCategory: item.majorCategory,
    minorCategory: item.minorCategory,
    actionStatus: item.actionStatus,
    resolutionTypeName: item.resolutionTypeName,
    actionSubStatusName: item.actionSubStatusName,
    actionName: item.actionName,
    originalTotal_formatted: formatMoney(item.originalTotal),
    currentTotal_formatted: formatMoney(item.currentTotal),
    proposedTotal_formatted: formatMoney(item.proposedTotal),
    currentContingent_formatted: String(item.currentContingent),
    currentUnliquidated_formatted: String(item.currentUnliquidated),
    currentDisputed_formatted: String(item.currentDisputed),
    officialClaimDate_formatted: item.officialClaimDate
      ? formatDate(item.officialClaimDate, ISO_DATE)
      : '-',
  };
}

export function formatScheduledClaimItem(
  item: ClaimRow,
): ScheduledClaimItemFormatted {
  const singleClaimUrl = buildSingleClaimPath(
    item.id!,
    SectionType.Claims,
    SubsectionType.AllClaims,
  );

  return {
    refLink: <Link to={singleClaimUrl}>{item.referenceNumber}</Link>,
    counterpartyName: item.counterpartyName,
    empty: 'empty',
    currentDebtor: item.currentDebtor,
    subMatchCode: item.subMatchCode,
    majorCategory: item.majorCategory,
    minorCategory: item.minorCategory,
    actionStatus: item.actionStatus,
    resolutionTypeName: item.resolutionTypeName,
    actionSubStatusName: item.actionSubStatusName,
    actionName: item.actionName,
    originalTotal_formatted: formatMoney(item.originalTotal),
    currentTotal_formatted: formatMoney(item.currentTotal),
    proposedTotal_formatted: formatMoney(item.proposedTotal),
    currentContingent_formatted: String(item.currentContingent),
    currentUnliquidated_formatted: String(item.currentUnliquidated),
    currentDisputed_formatted: String(item.currentDisputed),
  };
}

function createCommaSeparatedFullNames(
  contractsReviewers: ContractReviewerData,
): string {
  const fullNames: string[] = contractsReviewers?.userReviewers.map(
    (user) => `${user.firstName} ${user.lastName}`,
  );
  return fullNames.join(', ');
}

export function formatContractItem(
  item: ContractRowFormatted,
): ContractItemFormatted {
  //return only the values that we are using
  const singleClaimUrl = buildSingleContractPath(
    item.id!,
    SectionType.Claims,
    SubsectionType.AllClaims,
  );
  return {
    refLink: <Link to={singleClaimUrl}>{item.referenceNumber}</Link>,
    counterpartyName: item.counterpartyName,
    empty: 'empty',
    matchCode: item.matchCode,
    category: item.category,
    contractActionName: item.contractActionName,
    contractStatus: item.contractStatus,
    contractActionSubStatusName: item.contractActionSubStatusName,
    primaryDebtor: item.primaryDebtor,
    contractsReviewers: createCommaSeparatedFullNames(item.contractsReviewers!),
    rejectionDamageLow_formatted: formatMoney(item.rejectionDamageLow),
    rejectionDamagesHigh_formatted: formatMoney(item.rejectionDamagesHigh),
    description: item.description,
  };
}
